import { supabase } from '../supabaseClient';
import { CadernoSafetyError, cadernoRuntime, isMissingCadernoOptionalColumn } from './cadernoService';
import { toLocalIsoDate } from '../utils/localDate';
import { deleteCadernoRecord } from './cadernoDeletionService';

const EXPENSE_COLUMNS = [
  'id',
  'codigo',
  'conta_id',
  'propriedade_id',
  'talhao_id',
  'valor',
  'descricao',
  'data_despesa',
  'comprovante_path',
  'comprovante_nome_original',
  'comprovante_mime_type',
  'comprovante_mime_type_original',
  'comprovante_tamanho_bytes',
  'comprovante_tamanho_original_bytes',
  'comprovante_compressao',
  'comprovante_hash_sha256',
  'criado_por',
  'criado_em',
  'atualizado_em',
].join(',');
const ACTIVITY_EXPENSE_COLUMNS = ['ordem_id', 'categoria', 'adicional_ao_legado', 'client_request_id', 'estornado_em', 'estornado_por', 'motivo_estorno'];

function assertExpenseReadsEnabled() {
  if (!cadernoRuntime.readsEnabled) {
    throw new CadernoSafetyError(
      'A leitura real das despesas está disponível somente na sessão autenticada do Caderno.',
      'CADERNO_EXPENSE_READS_DISABLED'
    );
  }
}

function assertExpenseWritesEnabled() {
  if (!cadernoRuntime.writesEnabled) {
    throw new CadernoSafetyError(
      'A gravação de despesas está temporariamente desativada neste ambiente.',
      'CADERNO_EXPENSE_WRITES_DISABLED'
    );
  }
}

function errorDescription(error) {
  return [error?.message, error?.details, error?.hint]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function isCadernoExpenseSchemaUnavailable(error) {
  const description = errorDescription(error);
  return ['42p01', 'pgrst205'].includes(String(error?.code || '').toLowerCase())
    || (description.includes('caderno_despesas') && (
      description.includes('does not exist')
      || description.includes('not find')
      || description.includes('schema cache')
      || description.includes('relation')
    ));
}

export function parseCadernoExpenseValue(value) {
  const raw = String(value ?? '')
    .trim()
    .replace(/r\$/gi, '')
    .replace(/\s/g, '');
  const normalized = raw.includes(',')
    ? raw.replace(/\./g, '').replace(',', '.')
    : raw;
  const parsed = Number(normalized);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    throw new CadernoSafetyError(
      'Informe um valor de despesa maior que zero.',
      'CADERNO_EXPENSE_VALUE_INVALID'
    );
  }

  return Math.round((parsed + Number.EPSILON) * 100) / 100;
}

function expenseStorageError(error) {
  const description = errorDescription(error);
  if (description.includes('row-level security') || description.includes('rls')) {
    return new CadernoSafetyError(
      'O banco recusou o comprovante para este usuário ou propriedade.',
      'CADERNO_EXPENSE_RECEIPT_PERMISSION_DENIED'
    );
  }
  if (description.includes('bucket') && (description.includes('not found') || description.includes('does not exist'))) {
    return new CadernoSafetyError(
      'O armazenamento privado de comprovantes ainda não está configurado.',
      'CADERNO_EXPENSE_RECEIPT_BUCKET_MISSING'
    );
  }
  return new CadernoSafetyError(
    `Não foi possível enviar o comprovante.${error?.message ? ` ${error.message}` : ''}`,
    'CADERNO_EXPENSE_RECEIPT_UPLOAD_FAILED'
  );
}

export async function listCadernoExpenses(limit = null, { propertyId = null, plotIds = null } = {}) {
  assertExpenseReadsEnabled();
  const maximum = limit == null ? Infinity : Math.max(Number(limit) || 1, 1);
  const results = [];
  let withActivityColumns = true;
  while (results.length < maximum) {
    const pageSize = Math.min(200, maximum - results.length);
    let query = supabase.from('caderno_despesas')
      .select(withActivityColumns ? `${EXPENSE_COLUMNS},${ACTIVITY_EXPENSE_COLUMNS.join(',')}` : EXPENSE_COLUMNS)
      .order('data_despesa', { ascending: false })
      .order('criado_em', { ascending: false })
      .order('id', { ascending: false });
    if (propertyId) query = query.eq('propriedade_id', propertyId);
    if (plotIds) query = query.in('talhao_id', plotIds);
    const { data, error } = await query.range(results.length, results.length + pageSize - 1);
    if (withActivityColumns && isMissingCadernoOptionalColumn(error, ACTIVITY_EXPENSE_COLUMNS)) {
      withActivityColumns = false;
      continue;
    }
    if (error) throw error;
    results.push(...(data || []));
    if (!data || data.length < pageSize) break;
  }
  return results;
}

export async function createCadernoExpense(expense) {
  assertExpenseWritesEnabled();
  const description = String(expense?.descricao || '').trim();
  if (!expense?.propriedade_id) {
    throw new CadernoSafetyError('Selecione a propriedade da despesa.', 'CADERNO_EXPENSE_PROPERTY_REQUIRED');
  }
  if (description.length < 3) {
    throw new CadernoSafetyError(
      'Descreva a despesa com pelo menos 3 caracteres.',
      'CADERNO_EXPENSE_DESCRIPTION_REQUIRED'
    );
  }

  const payload = {
    propriedade_id: expense.propriedade_id,
    talhao_id: expense.talhao_id || null,
    valor: parseCadernoExpenseValue(expense.valor),
    descricao: description,
    data_despesa: expense.data_despesa || toLocalIsoDate(),
    ...(expense.ordem_id ? {
      ordem_id: expense.ordem_id,
      categoria: expense.categoria || 'outros',
      adicional_ao_legado: Boolean(expense.adicional_ao_legado),
    } : {}),
    ...(expense.client_request_id ? { client_request_id: expense.client_request_id } : {}),
  };
  const columns = expense.ordem_id ? `${EXPENSE_COLUMNS},${ACTIVITY_EXPENSE_COLUMNS.join(',')}` : EXPENSE_COLUMNS;
  const { data, error } = await supabase
    .from('caderno_despesas')
    .insert([payload])
    .select(columns)
    .single();

  if (error?.code === '23505' && expense.client_request_id) {
    const previous = await supabase.from('caderno_despesas').select(columns)
      .eq('client_request_id', expense.client_request_id).single();
    if (previous.error) throw previous.error;
    if (previous.data && Number(previous.data.valor) === payload.valor
      && previous.data.descricao === payload.descricao
      && String(previous.data.ordem_id || '') === String(payload.ordem_id || '')
      && String(previous.data.propriedade_id) === String(payload.propriedade_id)
      && String(previous.data.talhao_id || '') === String(payload.talhao_id || '')
      && previous.data.data_despesa === payload.data_despesa) return previous.data;
    throw new Error('Este envio ja foi registrado com outros dados. Confira os gastos da atividade antes de tentar novamente.');
  }
  if (isMissingCadernoOptionalColumn(error, ACTIVITY_EXPENSE_COLUMNS)) {
    throw new Error('A atualizacao de gastos por atividade ainda nao foi instalada no banco. Nenhum novo gasto foi registrado.');
  }
  if (error) throw error;
  return data;
}

export async function uploadCadernoExpenseReceipt({ propertyId, expenseId, receipt }) {
  assertExpenseWritesEnabled();
  const path = `${propertyId}/${expenseId}/${receipt.safeName}`;
  const { error } = await supabase.storage
    .from('caderno-comprovantes')
    .upload(path, receipt.file, {
      cacheControl: '3600',
      contentType: receipt.mimeType,
      upsert: false,
    });

  if (error && String(error.statusCode || error.status || '') !== '409') throw expenseStorageError(error);
  return path;
}

export async function reverseCadernoExpense(expenseId, reason) {
  assertExpenseWritesEnabled();
  if (String(reason || '').trim().length < 10) throw new Error('Informe uma justificativa com pelo menos 10 caracteres.');
  const { error } = await supabase.rpc('caderno_estornar_despesa', { p_despesa_id: expenseId, p_motivo: reason.trim() });
  if (error) throw error;
}

export async function deleteCadernoExpense(expenseId) {
  assertExpenseWritesEnabled();
  return deleteCadernoRecord('caderno_excluir_despesa', { p_despesa_id: expenseId });
}

export async function attachCadernoExpenseReceipt(expenseId, receipt, path) {
  assertExpenseWritesEnabled();
  const { data, error } = await supabase
    .from('caderno_despesas')
    .update({
      comprovante_path: path,
      comprovante_nome_original: receipt.name,
      comprovante_mime_type: receipt.mimeType,
      comprovante_mime_type_original: receipt.originalMimeType,
      comprovante_tamanho_bytes: receipt.size,
      comprovante_tamanho_original_bytes: receipt.originalSize,
      comprovante_compressao: receipt.compression,
      comprovante_hash_sha256: receipt.hashSha256,
    })
    .eq('id', expenseId)
    .select(EXPENSE_COLUMNS)
    .single();

  if (error) throw error;
  return data;
}

export async function downloadCadernoExpenseReceipt(path) {
  assertExpenseReadsEnabled();
  if (!path) {
    throw new CadernoSafetyError(
      'Esta despesa não possui comprovante anexado.',
      'CADERNO_EXPENSE_WITHOUT_RECEIPT'
    );
  }
  const { data, error } = await supabase.storage
    .from('caderno-comprovantes')
    .download(path);

  if (error) throw error;
  return data;
}
